/**
 * @module MfcChatCard
 * Add export default to end of file (Kiran 6/21/21)
 * Update image_url to work with app.mfc (Kiran 6/21/21)
 * Update apiUrl to work with app.mfc in worker (Marc 11/15/22)
 */

export interface Card {
    description?: string
    formatted_username?: string
    image_url?: string
    image?: string
    logo?: string
    mfcshare?: 0 | 1
    title?: string
    token_amount?: number
    type?: string
    url_domain?: string
    url?: string
    user_id?: number
    username_style?: string
    username_url?: string
    username?: string
}
export interface CardTemplate {
    card: Card
    html: string
}
export interface ChatCardResponse {
    data_enc: string
    data_tkx: string
}
export interface MfcChatCardOptions {
    /** a function that is called when api.mfc returns a chat card data object (required) */
    callback_func?: (oCard: ChatCardResponse) => void
    get_formatted_username_func?: (nUserId: number, sUsername: string) => string
    /** a function that will accept a user id, and in turn supply MfcChatCard with a css styling string for that user (optional) */
    get_user_style_func?: (nUserId: number) => string
    share_tpl_func?: (oCard: CardTemplate) => CardTemplate
    hostname?: string
}

const MFC_SHARE_CARD_TEMPLATE = `\
<div class="MFCShare_inChat">\
<table border="0">\
<tr>\
<td>\
<a href="{{url}}" target="_blank">\
<img onError="this.style.display='none'" class="MFCShare_inChat_img" src="{{image}}" />\
</a>\
</td>\
<td class="MFCShare_inChat_content">\
<a href="{{username_url}}" target="_blank">\
<div class="MFCShare_inChat_username" style="{{username_style}}">\
{{formatted_username}}&nbsp;\
</div>\
</a>\
<a href="{{url}}" target="_blank">\
<span class="MFCShare_inChat_title">{{title}}&nbsp;</span>\
</a>\
<div class="MFCShare_inChat_logo">\
on&nbsp;\
<a href="{{url}}" target="_blank">\
<img onError="this.style.display='none'" src="{{logo}}" />\
</a>\
</div>\
</td>\
</tr>\
</table>\
</div>\
`

const TWITTER_CARD_TEMPLATE = `\
<div class="TwitterCard_inChat">\
<table border="0">\
<tr>\
<td class="TwitterCard_inChat_img_td">\
<a href="{{url}}" target="_blank">\
<img onError="this.style.display='none'" class="TwitterCard_inChat_img" src="{{image}}" />\
</a>\
</td>\
<td>\
<div class="TwitterCard_inChat_title">\
<a href="{{url}}" target="_blank">\
<span class="TwitterCard_inChat_title">{{title}}</span>\
</a>\
</div>\
<div class="TwitterCard_inChat_description">\
<a href="{{url}}" target="_blank">{{description}}</a>\
</div>\
<a class="TwitterCard_inChat_domain" href="{{url}}">{{domain}}</a>\
</td>\
</tr>\
</table>\
</div>\
`

/**
    This object receives chat lines and looks for a URL that qualifies to be processed like a Twitter Card,
    where a box is displayed in a chat room, which contains a thumbnail, title and description of whatever 
    is at the other end of the URL. 

    An outgoing chat line is passed to the method ProcessChatInput(), and if a qualified URL is found in the chat
    line, an API call is made to https://api-edge.myfreecams.com/chatcard/?url=[url_encoded_url]

    The response is a JSON object which contains two important keys, `data_enc` and `data_tkx`, which the client
    serializes in a JSON string and passes along to FCServer in an FCS.FCTYPE_XMESG (89) command.

    `data_enc` is a base64 encoded JSON string containing the chat card key value pairs, and `data_tkx` is a
    security token that is derived from "data_enc", and is used to ensure that `data_enc` travels from the API
    response to FCServer without modification in between. In addition to `data_enc` and `data_tkx`,
    `"to": [room channel id]` and `"type": "card"` are also passed along in the XMESG.

    FCServer then sends FCS.FCTYPE_XMESG's to all the users in that channel. The command will contain JSON in the 
    payload, in which there is a key called "data" that contains a sub object with the chat card key values. 
    payload.data is then passed into to a method of MfcChatCard called `ProcessCard` which returns an html 
    string of that chat card. This html has class values that can be specified in a seperate css file. 

    This is set up to support MFC Share, YouTube, Twitter and Facebook, but it currently only enabled for MFC Share.

    - Required key for constructor object:
      - `callback_func`: a function that is called when api.mfc returns a chat card data object

    - Optional key for constructor object:
      - `get_user_style_func`: a function that will accept a user id, and in turn supply MfcChatCard with a css styling string for that user. 

    @example
    ```js
    // Sending functions...

    // client's funciton for sending a CMESG to the FCServer
    function SendChat(sChatMsg, aMsg) {
        // instatiate MfcChatCard, providing a callback to receive the card data from api.mfc
        const mfcChatCard = new MfcChatCard({ callback_func: mfcChatCard_callback })
        // if sChatMsg contains a qualified URL, mfcChatCard will make an API call and trigger the callback
        mfcChatCard.ProcessChatInput(sChatMsg)
        // send the chat message to the server as usual
        TxCmd(t.FCS.FCTYPE_CMESG, t.g_nSessionId, t.g_hoPlayerWindows[g_nBroadcasterId]['room_channel'], 0, 0, sChatMsg)
    }

    // create a callback that will receive the chat card data, and pass it along to FCServer as an XMESG
    function mfcChatCard_callback(oCard) {
        // create a JSON string with the four key:value pairs: to, type, data_enc and data_tkx
        const sJSON = JSON.stringify({
            to: t.g_hoPlayerWindows[g_nBroadcasterId]['room_channel'],
            type: 'card',
            data_enc: oCard.data_enc,
            data_tkx: oCard.data_tkx,
        })
        // send a FCS.FCTYPE_XMESG with that JSON string in the payload (the 6th argument of a command)
        TxCmd(FCS.FCTYPE_XMESG, t.g_nSessionId, t.g_hoPlayerWindows[g_nBroadcasterId]['room_channel'], 0, 0, sJSON)
    }

    // Receiving functions...

    // handler to receive FCS.FCTYPE_XMESG commands from the server
    t.events.onRx(FCS.FCTYPE_XMESG, function (fcMsg) {
        FcXMessage(fcMsg)
    })

    function FcXMessage(fcMsg) {
        // make sure fcMsg.obj.data is present before going further
        if (!fcMsg || !fcMsg.obj || !fcMsg.obj.data) {
            console.log('FcXMessage not processed')
            return
        }
        // make sure the XMESG is for this chat room, or is otherwise routed to the correct chat room
        if (t.GetRoomOwnerId(fcMsg.arg1) !== g_nBroadcasterId) {
            return
        }
        // create an instance of MfcChatCard to produce the chat card HTML
        const mfcChatCard = new MfcChatCard({
             // provide a user css styling string function
            get_user_style_func: function (nUserId) {
                return MfcCustomFonts.prototype.get_style(t.g_hUsers[nUserId])
            },
        })
        // produce the chat card HTML
        const sHTML = mfcChatCard.ProcessCard(fcMsg.obj.data)
        // append this HTML to the chat room
        ChatQueue.Append(sHTML)
    }
    ```

    @example
    Example CSS
    ```css
    .MFCShare_inChat {
        position: relative;
        border: 2px solid #008000;
        border-radius: 8px;
        background-color: #FBFBFB;
        margin: 5px 0px;
    }
    .MFCShare_inChat table {
        width: 100%;
        height: 35px;
        border-spacing: 0px;
        border-collapse: separate;
    }
    .MFCShare_inChat td {
        vertical-align: top;
        position: relative;
    }
    .MFCShare_inChat_content {
        width: 100%;
    }
    .MFCShare_inChat a {
        text-decoration: none !important;
    }
    .MFCShare_inChat_logo {
        display: inline-block;
        color: black;
        font-size: 12px;
    }
    .MFCShare_inChat_logo img {
        height: 10px;
    }
    .MFCShare_inChat_img {
        height: 35px;
        border: 1px inset #DDD;
        border-radius: 5px 0px 0px 5px;
        max-width:none !important;
    }
    .MFCShare_inChat_type {
        color: black;
        font-size: 12px;
    }
    .MFCShare_inChat_token_amount {
        color: black;
        font-size: 12px;
    }
    .MFCShare_inChat_title {
        color: #008000;
        font-size: 13px;
    }
    .MFCShare_inChat_username {
        font-weight: bold;
        font-family: "Noto Sans","Verdana","Montserrat","Helvetica Neue", Helvetica, Arial, sans-serif;
        display: inline;
        font-size: 16px;
    }
    .MFCShare_inChat_url {
        display: inline;
        font-size: 12px;
        color: gray;
    }
    .TwitterCard_inChat {
        position: relative;
        border: 2px solid #008000;
        border-radius: 8px;
        background-color: #FBFBFB;
    }
    .TwitterCard_inChat table {
        width: 100%;
        height: 50px;
        border-spacing: 0px;
        border-collapse: separate;
    }
    .TwitterCard_inChat td {
        vertical-align: top;
        position: relative;
    }
    .TwitterCard_inChat_content {
        width: 100%;
        padding: 2px 10px 21px 10px;
    }
    .TwitterCard_inChat a {
        text-decoration: none !important;
    }
    .TwitterCard_inChat_domain {
        position: absolute;
        right: 15px;
        bottom: 5px;
        height: 10px;
    }
    .TwitterCard_inChat_domain {
        position: absolute;
        font-size: 11px;
        font-weight: bold;
        right: 15px;
        bottom: 5px;
        height: 10px;
    }
    .TwitterCard_inChat_img {
        height: 50px;
        border: 1px inset #DDD;
        border-radius: 5px 0px 0px 5px;
        max-width: none !important;
    }
    .TwitterCard_inChat_title {
        color: black;
        font-weight: bold;
        font-size: 13px;
    }
    .TwitterCard_inChat_description {
        color: #555;
        font-size: 10px;
        padding: 5px;
    }
    .TwitterCard_inChat_url {
        display: inline;
        font-size: 12px;
        color: gray;
    }
    ```
*/
export class MfcChatCard {
    conf: MfcChatCardOptions
    apiUrl: string
    oReq: XMLHttpRequest

    constructor(conf: MfcChatCardOptions) {
        this.conf = conf || {}

        this.apiUrl =
            '//api-edge.' +
            (typeof document === 'undefined'
                ? this.conf.hostname
                : document.location.hostname
            ).replace(/.*?(\w+\.\w+)$/, '$1')

        this.oReq = new XMLHttpRequest()
        this.oReq.withCredentials = true
    }

    /**
     * Creates the chat card and triggers the `callback_func` callback passed to the constructor.
     */
    ProcessChatInput(sChatLine: string) {
        if (typeof sChatLine !== 'string') {
            return
        }

        const re = /(https:\/\/)(share\.myfreecams\.com|mfcsha\.re)([\S]+)/i
        let aMatches: RegExpMatchArray | null = null
        if (!(aMatches = sChatLine.match(re))) {
            return
        }

        aMatches[2] = aMatches[2].replace(/mfcsha\.re/i, 'share.myfreecams.com')
        const url = aMatches[1] + aMatches[2] + aMatches[3]

        const reqUrl = this.apiUrl + '/chatcard/?url=' + encodeURIComponent(url)

        this.oReq = new XMLHttpRequest()
        this.oReq.open('get', reqUrl)
        this.oReq.onreadystatechange = () => {
            this.ProcessChatInput_callback()
        }
        this.oReq.send(null)
    }

    ProcessChatInput_callback() {
        if (this.oReq.readyState !== 4) {
            return
        }

        let oRes: { result: CardTemplate | undefined } | undefined
        try {
            oRes = JSON.parse(this.oReq.responseText)
        } catch (e) {
            console.log(e)
        }

        if (!oRes || !oRes.result) {
            console.log(this.oReq.responseText)
            return
        }

        if (
            this.conf.callback_func &&
            typeof this.conf.callback_func === 'function'
        )
            this.conf.callback_func.apply(this, [oRes.result])
    }

    ProcessCard(hCard: Card) {
        let hTpl = {} as CardTemplate
        let sCard: string

        if (
            hCard.url &&
            (hCard.url.match(/(https:\/\/share\.myfreecams\.com\/)/i) ||
                hCard.url.match(/(https:\/\/mfcsha\.re\/)(\w+)(\/\w+)/i))
        ) {
            hTpl = this.MfcShare_CardTpl(hCard)
            if (this.conf.share_tpl_func) {
                hTpl = this.conf.share_tpl_func(hTpl)
            }
        } else if (hCard.url) {
            hTpl = MfcChatCard.CardTpl(hCard)
        }

        if (hTpl.html && typeof hTpl.card === 'object') {
            for (const key in hTpl.card) {
                if (
                    typeof hTpl.card[key] !== 'string' &&
                    typeof hTpl.card[key] !== 'number'
                ) {
                    continue
                }
                const idx: string = key
                const regex = new RegExp('{{' + key + '}}', 'g')
                hTpl.html = hTpl.html.replace(regex, hTpl.card[idx])
            }

            sCard = hTpl.html.replace(/{{\s*\w+\s*}}/g, '')
        }

        return sCard
    }

    MfcShare_CardTpl(hCard: Card) {
        hCard = hCard || {}

        hCard.mfcshare = 1

        hCard.url_domain = (
            typeof document === 'undefined'
                ? this.conf.hostname
                : document.location.hostname
        ).replace(/.*?(\w+\.\w+)$/, '$1')

        hCard.username = hCard.username && hCard.username.replace(/[^\w]/g, '')
        hCard.username_url =
            hCard.username &&
            'https://share.' +
                hCard.url_domain +
                '/' +
                hCard.username.replace(/[^\w]/g, '')
        hCard.title = (hCard.title && hCard.title.replace(/[<]/g, '')) || ''
        hCard.description =
            (hCard.description && hCard.description.replace(/[<]/g, '')) || ''
        hCard.image =
            (hCard.image &&
                hCard.image.replace(/"/g, '').replace(/\?.*/, '')) ||
            ''

        const image_url = hCard.image_url || 'https://assets.mfcimg.com/'
        hCard.logo = image_url + 'images/svg/MFC_Share.svg'

        let sExtra = ''
        if (hCard.type) {
            sExtra +=
                '<span class=MFCShare_inChat_type>' + hCard.type + '</span>'
        }
        if (hCard.token_amount) {
            if (sExtra) {
                sExtra += ', '
            }
            sExtra +=
                '<span class=MFCShare_inChat_token_amount>' +
                hCard.token_amount +
                ' tokens</span>'
        }

        hCard.title = '<b>' + hCard.title + '</b>'
        if (sExtra) {
            hCard.title += ' ' + sExtra
        }

        const hTpl = {} as CardTemplate
        hTpl.card = hCard

        if (typeof this.conf.get_user_style_func === 'function') {
            hTpl.card.username_style = this.conf.get_user_style_func(
                hCard.user_id,
            )
        } else {
            hTpl.card.username_style = ''
        }

        if (typeof this.conf.get_formatted_username_func === 'function') {
            hTpl.card.formatted_username =
                this.conf.get_formatted_username_func(
                    hCard.user_id,
                    hCard.username,
                )
        } else {
            hTpl.card.formatted_username = hCard.username
        }

        hTpl.html = (() => MFC_SHARE_CARD_TEMPLATE)()

        return hTpl
    }

    static CardTpl(hCard: Card) {
        const hTpl: CardTemplate = {
            card: hCard,
            html: (() => TWITTER_CARD_TEMPLATE)(),
        }
        return hTpl
    }
}
